import { useRef } from "react";
import { Box, BoxProps, Stack } from "@mui/material";
import dynamic from "next/dynamic";
import { useTranslation } from "next-i18next";
import { ITradeHistoryTableDict } from "src/api/server/wf-detail.types";
import { Button } from "src/components-mui/base/button/button";
import { WfHeading } from "src/components-mui/base/wf-heading/wf-heading";
import { EmptyState } from "src/components-mui/common/empty-state/empty-state";
import { ErrorState } from "src/components-mui/pages/detail/shared/error-state";
import { LoadingState } from "src/components-mui/pages/detail/trade-history/loading-state";
import { TimeSpan } from "src/components-mui/pages/detail/trade-history/time-span";
import { ITradeHistoryTableProps } from "src/components-mui/pages/detail/trade-history/trade-history-table";
import { useTradeHistoryTable } from "src/components-mui/pages/detail/trade-history/use-trade-history-table";

const TradeHistoryTable = dynamic<ITradeHistoryTableProps>(
    () => import("src/components-mui/pages/detail/trade-history/trade-history-table").then(component => component.TradeHistoryTable),
    {
        loading: () => <LoadingState />,
    }
);

const PAGINATION_BUTTON_SX = {
    "&.MuiButton-sizeExtraSmall, &.MuiButton-sizeSmall": { lineHeight: 1.2 },
    "&.Mui-disabled": { borderColor: "gray.200", pointerEvents: "auto", "&:hover": { backgroundColor: "transparent" } },
};

type TTradeHistoryTableState = ReturnType<typeof useTradeHistoryTable>;

interface ITradeHistoryContentProps extends TTradeHistoryTableState {
    dict: ITradeHistoryTableDict;
}

const TradeHistoryContent = ({ isLoading, isError, data, newerBtnProps, olderBtnProps, dict }: ITradeHistoryContentProps) => {
    const { t } = useTranslation("wf-detail");

    if (isError) {
        return (
            <ErrorState>
                <LoadingState isAnimated={false} />
            </ErrorState>
        );
    }

    if (!data) {
        return <LoadingState isAnimated={isLoading} />;
    }

    if (data.tradeHistory.orders.length === 0) {
        return <EmptyState {...dict.empty} />;
    }

    return (
        <>
            <TradeHistoryTable orders={data.tradeHistory.orders} isSuperWikifolio={data.tradeHistory.isSuperWikifolio} />
            <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "center", gap: 1, mt: [3, 3, 5] }}>
                <Button variant="outlined" size={["extraSmall", "small"]} sx={PAGINATION_BUTTON_SX} className="gtm-newer-history" {...newerBtnProps}>
                    {t("newer")}
                </Button>
                <TimeSpan orders={data.tradeHistory.orders} />
                <Button variant="outlined" size={["extraSmall", "small"]} sx={PAGINATION_BUTTON_SX} className="gtm-older-history" {...olderBtnProps}>
                    {t("older")}
                </Button>
            </Stack>
        </>
    );
};

interface ITradeHistorySectionProps extends BoxProps {
    wikifolioId: string;
    dict: ITradeHistoryTableDict;
}

export const TradeHistorySection = ({ wikifolioId, dict, ...boxProps }: ITradeHistorySectionProps) => {
    const sectionRef = useRef<HTMLDivElement>(null);
    const tableState = useTradeHistoryTable(wikifolioId, sectionRef);

    return (
        <Box component="section" ref={sectionRef} {...boxProps}>
            <WfHeading component="h2" fontSize="2xl" sx={{ mb: 3 }}>
                {dict.title}
            </WfHeading>
            <TradeHistoryContent {...tableState} dict={dict} />
        </Box>
    );
};
